import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  PersonSimpleCircle,
  X,
  Plus,
  Minus,
  ArrowCounterClockwise,
  TextAa,
  Sun,
  CircleHalf,
  Drop,
  LinkSimple,
  Cursor,
  FilmSlate,
} from '@phosphor-icons/react';
import './AccessibilityWidget.css';

const STORAGE_KEY = 'clinikit_a11y_settings';
const MIN_STEP = 0;
const MAX_STEP = 4; // 5 levels: 0 (100%) .. 4 (140%)
const ZOOM_PER_STEP = 0.1;

const DEFAULT_SETTINGS = {
  fontStep: 0,
  contrast: false,
  invert: false,
  grayscale: false,
  highlightLinks: false,
  readableFont: false,
  stopAnimations: false,
  bigCursor: false,
};

function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch (error) {
    // [CK-6910] Failed reading accessibility settings from storage
    console.error('[CK-6910] a11y settings load failed', {
      errorCode: 'CK-6910',
      errorMessage: error?.message,
      timestamp: new Date().toISOString(),
    });
    return { ...DEFAULT_SETTINGS };
  }
}

// Build combined CSS filter string from visual settings.
function buildFilter(s) {
  const parts = [];
  if (s.grayscale) parts.push('grayscale(1)');
  if (s.contrast) parts.push('contrast(1.35)');
  if (s.invert) parts.push('invert(1) hue-rotate(180deg)');
  return parts.join(' ');
}

// Apply settings to the app root (#root) + <html>. The widget itself lives in a
// separate portal node (#a11y-root) outside #root, so it is never filtered/zoomed.
function applySettings(s) {
  const root = document.getElementById('root');
  if (root) {
    root.style.filter = buildFilter(s);
    root.style.zoom = s.fontStep > 0 ? String(1 + s.fontStep * ZOOM_PER_STEP) : '';
  }
  const html = document.documentElement;
  html.classList.toggle('a11y-highlight-links', s.highlightLinks);
  html.classList.toggle('a11y-readable-font', s.readableFont);
  html.classList.toggle('a11y-stop-animations', s.stopAnimations);
  html.classList.toggle('a11y-big-cursor', s.bigCursor);
}

function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(loadSettings);
  const portalRef = useRef(null);
  const drawerRef = useRef(null);
  const toggleBtnRef = useRef(null);

  // Dedicated portal host outside #root so effects never touch the widget.
  if (!portalRef.current && typeof document !== 'undefined') {
    let node = document.getElementById('a11y-root');
    if (!node) {
      node = document.createElement('div');
      node.id = 'a11y-root';
      document.body.appendChild(node);
    }
    portalRef.current = node;
  }

  // Apply + persist whenever settings change.
  useEffect(() => {
    applySettings(settings);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      // [CK-6911] Failed persisting accessibility settings
      console.error('[CK-6911] a11y settings save failed', {
        errorCode: 'CK-6911',
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
    }
  }, [settings]);

  // Esc closes the drawer; restore focus to the toggle.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        toggleBtnRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    // move focus into the drawer for keyboard users
    drawerRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const toggle = useCallback((key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const changeFont = useCallback((dir) => {
    setSettings((prev) => ({
      ...prev,
      fontStep: Math.min(MAX_STEP, Math.max(MIN_STEP, prev.fontStep + dir)),
    }));
  }, []);

  const reset = useCallback(() => setSettings({ ...DEFAULT_SETTINGS }), []);

  const toggles = [
    { key: 'contrast', label: 'ניגודיות גבוהה', icon: CircleHalf },
    { key: 'invert', label: 'היפוך צבעים', icon: Sun },
    { key: 'grayscale', label: 'גווני אפור', icon: Drop },
    { key: 'highlightLinks', label: 'הדגשת קישורים', icon: LinkSimple },
    { key: 'readableFont', label: 'פונט קריא', icon: TextAa },
    { key: 'stopAnimations', label: 'עצירת אנימציות', icon: FilmSlate },
    { key: 'bigCursor', label: 'סמן גדול', icon: Cursor },
  ];

  if (!portalRef.current) return null;

  return createPortal(
    <>
      <button
        ref={toggleBtnRef}
        type="button"
        className="a11y-fab"
        aria-label="תפריט נגישות"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <PersonSimpleCircle size={26} weight="fill" />
      </button>

      {open && <div className="a11y-overlay" onClick={() => setOpen(false)} />}

      <div
        ref={drawerRef}
        className={`a11y-drawer${open ? ' open' : ''}`}
        role="dialog"
        aria-label="הגדרות נגישות"
        aria-modal="true"
        tabIndex={-1}
      >
        <div className="a11y-drawer-header">
          <h2>נגישות</h2>
          <button
            type="button"
            className="a11y-close"
            aria-label="סגירת תפריט נגישות"
            onClick={() => setOpen(false)}
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="a11y-drawer-body">
          {/* Text size */}
          <div className="a11y-fontsize">
            <span className="a11y-fontsize-label">
              <TextAa size={20} />
              גודל טקסט
            </span>
            <div className="a11y-fontsize-controls">
              <button
                type="button"
                aria-label="הקטנת טקסט"
                onClick={() => changeFont(-1)}
                disabled={settings.fontStep <= MIN_STEP}
              >
                <Minus size={16} weight="bold" />
              </button>
              <span className="a11y-fontsize-value">
                {100 + settings.fontStep * 10}%
              </span>
              <button
                type="button"
                aria-label="הגדלת טקסט"
                onClick={() => changeFont(1)}
                disabled={settings.fontStep >= MAX_STEP}
              >
                <Plus size={16} weight="bold" />
              </button>
            </div>
          </div>

          {/* Toggles */}
          <div className="a11y-toggles">
            {toggles.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                className={`a11y-toggle${settings[key] ? ' active' : ''}`}
                aria-pressed={settings[key]}
                onClick={() => toggle(key)}
              >
                <Icon size={22} weight={settings[key] ? 'fill' : 'regular'} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <button type="button" className="a11y-reset" onClick={reset}>
            <ArrowCounterClockwise size={18} weight="bold" />
            איפוס הגדרות
          </button>

          <Link
            to="/accessibility"
            className="a11y-statement-link"
            onClick={() => setOpen(false)}
          >
            הצהרת נגישות
          </Link>
        </div>
      </div>
    </>,
    portalRef.current
  );
}

export default AccessibilityWidget;
